import { 
  MutationFunction, 
  useMutation, 
  UseMutationResult, 
  useQueryClient 
} from '@tanstack/react-query'

interface UseOptimisticUpdateOptions {
  mutationFn: MutationFunction
  onErrorActionCallback?: () => void
  onSuccessActionCallback?: () => void
  queryKey: string
}

export const useOptimisticUpdate = <T>({ 
  mutationFn, 
  onErrorActionCallback,
  onSuccessActionCallback,
  queryKey
}: UseOptimisticUpdateOptions): UseMutationResult => {
  const queryClient = useQueryClient()

  return useMutation(
    mutationFn,
    {
      onMutate: async (data: T) => {
        await queryClient.cancelQueries()
        const previousValue = queryClient.getQueryData([queryKey])

        if (previousValue) {
          queryClient.setQueryData([queryKey], data)
        }

        return { previousValue }
      },
      onError: (error, variables, context) => {
        onErrorActionCallback()
        if (context?.previousValue) {
          queryClient.setQueryData([queryKey], context.previousValue)
        }
      },
      onSettled: () => {
        queryClient.invalidateQueries([queryKey])
      },
      onSuccess: () => {
        onSuccessActionCallback()
      }
    }
  )
}